import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Dev console + whatever analytics we have. We deliberately don't ship
    // error messages to users — the UI below shows only the page they can
    // recover from.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="error-page">
        <pre className="error-code" aria-hidden="true">{`
  5 0 0
  ─────
  ▁▃▂▇▅▆▁▃█▂▅▁
`}</pre>
        <h1>Something went wrong.</h1>
        <p className="error-detail">
          SmpleCut hit an error it couldn't recover from. The team's been
          notified. Try again, or head back to the home page.
        </p>
        {import.meta.env.DEV && (
          <details className="error-details">
            <summary>Developer details</summary>
            <pre className="error-stack">{this.state.error.stack ?? this.state.error.message}</pre>
          </details>
        )}
        <div className="error-actions">
          <button onClick={this.handleReset} className="btn btn-primary">
            Try again
          </button>
          {/* Full reload escape hatch in case the error is from bad global state. */}
          <a href="/" className="btn">Back to home</a>
        </div>
      </div>
    );
  }
}
